import invariant from './invariant';

const emptyFn = () => {};
const getId = object => object.id;

const hasOwnProperty = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj, key);

const isFunctionOrString = value =>
  ['function', 'string'].includes(typeof value);

/**
 * A shallow normalizer that transforms an array of items into an object while
 * providing some flexibility around the shape of the normalized object.
 * Normalization happens at one level deep, so no connection is made between
 * entities, hence the name.
 *
 * @example
 * const normalizer = new ShallowNormalizer({
 *  source: [
 *    { id: 1, name: "foo" },
 *    { id: 2, name: "bar" },
 *  ]
 * });
 *
 * normalizer.normalize();
 * // outputs:
 * {
 *   "1": { id: 1, name: "foo" },
 *   "2": { id: 2, name: "bar" },
 * }
 *
 * See the test file for more detailed examples.
 */
export default class ShallowNormalizer {
  /**
   * @param {Object} options
   * @param {Object[]} options.source - An array of objects to normalize
   * @param {string|function} [options.identifier=id] - The name of a property
   * whose value will be used as a unique identifier for all items of the source
   * array, or a function that takes each item of the source array, and returns
   * a custom unique value for each one.
   * @param {string|function} [options.groupBy] - The name of a property whose
   * value will be used as the key to group all items by. This results in an
   * object whose keys are the name of the groups. Each group is a normalized
   * object itself.
   */
  constructor({ source, identifier = getId, groupBy = emptyFn } = {}) {
    invariant(
      Array.isArray(source),
      'Expected source option to be of type Array but instead received ' +
        `${typeof source}`,
    );

    invariant(
      isFunctionOrString(identifier),
      'Expected identifier option to be of type string or function but ' +
        `instead received ${typeof identifier}`,
    );

    invariant(
      isFunctionOrString(groupBy),
      'Expected groupBy option to be of type string or function but ' +
        `instead instead ${typeof groupBy}`,
    );

    this.source = source;

    if (typeof identifier === 'string')
      this.getIdentifierKey = item => item[identifier];
    else this.getIdentifierKey = identifier;

    if (typeof groupBy === 'string') this.getGroupKey = item => item[groupBy];
    else this.getGroupKey = groupBy;
  }

  normalize() {
    const normalized = {};
    let hasWarnedAboutDuplication = false;

    this.source.forEach(item => {
      const identifierKey = this.getIdentifierKey(item);
      const groupKey = this.getGroupKey(item);

      let target;
      if (groupKey) {
        if (!normalized[groupKey]) {
          normalized[groupKey] = {};
        }
        target = normalized[groupKey];
      } else {
        target = normalized;
      }

      // Warn once if encountered duplicate identifierKeys
      if (
        process.env.NODE_ENV !== 'production' &&
        !hasWarnedAboutDuplication &&
        hasOwnProperty(target, identifierKey)
      ) {
        hasWarnedAboutDuplication = true;
        console.warn(
          "Expected a unique value of 'identifer' for each object in " +
            `${groupKey ? 'the normalized object' : 'a normalized group'}, ` +
            'but instead found duplicate values. Duplicate items will be ' +
            'overwritten to keep just one.',
        );
      }

      target[identifierKey] = item;
    });

    return normalized;
  }
}
